// ─── Importare i soci dalla rubrica Google e da Matchpoint ───────────────────
//
// 🗣️ Voce 26 (25/09/2026, dal mockup approvato): i due file insieme, i
// doppioni uniti, e niente si scrive finché non lo si è guardato riga per riga
// (⇒ `@pmo/shared/import-soci.ts`, dove stanno le regole).
//
// 🚨 Come l'import della rubrica che c'era prima: il primo bottone LEGGE E
// BASTA. Sono spuntati da soli solo i nuovi che arrivano da Matchpoint; chi sta
// solo nella rubrica si sceglie a mano, perché in una rubrica ci sono anche il
// dentista e l'idraulico.

import { useState } from 'react';
import { siSpuntaDaSola, type EsitoImportFile, type RigaImport } from '@pmo/shared';
import { usaImportFile } from '../../features/soci/dati.js';
import { Guasto } from '../../components/pezzi.js';

type FileLetto = { nome: string; contenuto: string };

/** In base64 a pezzi: un file grosso passato tutto insieme a `btoa` fa saltare la pila. */
async function inBase64(f: File): Promise<FileLetto> {
  const byte = new Uint8Array(await f.arrayBuffer());
  let binario = '';
  for (let i = 0; i < byte.length; i += 0x8000)
    binario += String.fromCharCode(...byte.subarray(i, i + 0x8000));
  return { nome: f.name, contenuto: btoa(binario) };
}

const DICITURA: Record<RigaImport['esito'], string> = {
  nuovo: 'nuovi',
  aggiorna: 'da aggiornare',
  uguale: 'uguali',
  scartato: 'scartate',
};
const FONTE = { M: 'Matchpoint', G: 'rubrica Google' } as const;

export function ImportaFile({ chiudi }: { chiudi: () => void }): React.JSX.Element {
  const leggi = usaImportFile();
  const scrivi = usaImportFile();
  const [scelti, setScelti] = useState<{ G: File | null; M: File | null }>({ G: null, M: null });
  const [letti, setLetti] = useState<FileLetto[]>([]);
  const [esito, setEsito] = useState<EsitoImportFile | null>(null);
  const [spunte, setSpunte] = useState<Set<string>>(new Set());
  const fase = scrivi.data?.applicato ? 3 : esito ? 2 : 1;

  const righe = esito?.righe ?? [];
  const conta = (e: RigaImport['esito']) => righe.filter((r) => r.esito === e).length;
  const aggiornabili = righe.filter((r) => r.esito === 'aggiorna');
  const soloRubrica = righe.filter((r) => r.esito === 'nuovo' && !r.da.includes('M'));
  const aggiungi = (quali: RigaImport[]) => setSpunte(new Set([...spunte, ...quali.map((r) => r.chiave)]));

  const guarda = async () => {
    const file = await Promise.all([scelti.G, scelti.M].filter((f): f is File => f !== null).map(inBase64));
    setLetti(file);
    leggi.mutate(
      { file, apply: false, scelte: [] },
      {
        onSuccess: (e) => {
          setEsito(e);
          setSpunte(new Set(e.righe.filter(siSpuntaDaSola).map((r) => r.chiave)));
        },
      },
    );
  };
  const daCapo = () => {
    setEsito(null);
    setSpunte(new Set());
    leggi.reset();
    scrivi.reset();
  };

  return (
    <section className="riquadro importa-soci">
      <h3>Importa soci da un file</h3>
      <ol className="passi">
        {['Scegli i file', 'Guarda cosa entra', 'Fatto'].map((t, i) => (
          <li key={t} aria-current={fase === i + 1 ? 'step' : undefined}>
            {i + 1} · {t}
          </li>
        ))}
      </ol>

      {fase === 1 && (
        <>
          <p className="tenue piccolo">
            Si mettono dentro tutti e due i file insieme: chi sta in tutti e due diventa una persona sola, e
            chi è già nell’anagrafica non si duplica. Nessuna riga si scrive finché non la guardi.
          </p>
          <div className="fonti">
            {(['G', 'M'] as const).map((k) => (
              <div key={k} className={`fonte${scelti[k] ? ' scelto' : ''}`}>
                <strong>{k === 'G' ? 'Rubrica Google' : 'Matchpoint'}</strong>
                <span className="piccolo tenue">
                  {k === 'G'
                    ? 'il CSV di Google Contatti → Esporta → «Google CSV»'
                    : 'l’esportazione dei clienti, Excel o CSV'}
                </span>
                <label className="scegli-file">
                  <input
                    type="file"
                    accept={k === 'G' ? '.csv,text/csv' : '.xlsx,.csv'}
                    onChange={(e) => setScelti({ ...scelti, [k]: e.target.files?.[0] ?? null })}
                  />
                  <span>
                    {scelti[k] ? `✓ ${scelti[k].name}` : k === 'G' ? 'Scegli il CSV' : 'Scegli il file'}
                  </span>
                </label>
              </div>
            ))}
          </div>
          <Guasto errore={leggi.error} />
          <div className="azioni">
            <button
              type="button"
              disabled={(!scelti.G && !scelti.M) || leggi.isPending}
              onClick={() => void guarda()}
            >
              {leggi.isPending ? 'Sto leggendo…' : 'Guarda cosa entra'}
            </button>
            <button type="button" className="bordato" onClick={chiudi}>
              Lascia stare
            </button>
          </div>
        </>
      )}

      {fase === 2 && esito && (
        <>
          <p className="piccolo">
            {esito.file.map((f, i) => (
              <span key={f.nome}>
                {i > 0 && ' + '}
                <strong>{f.nome}</strong> ({FONTE[f.fonte]}, {f.righe} righe)
              </span>
            ))}{' '}
            ⇒ <strong>{righe.length} persone</strong>
          </p>
          <div className="conti">
            {(['nuovo', 'aggiorna', 'uguale', 'scartato'] as const).map((e) => (
              <span key={e} className={`pillola ${e}`}>
                {conta(e)} {DICITURA[e]}
              </span>
            ))}
          </div>
          <div className="tabella-scorre">
            <table className="righe-import">
              <thead>
                <tr>
                  <th>
                    <span className="piccolo">scegli</span>
                  </th>
                  <th>Da</th>
                  <th>Nome</th>
                  <th>Telefono</th>
                  <th>Email</th>
                  <th>Cosa succede</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => {
                  const sceglibile = r.esito === 'nuovo' || r.esito === 'aggiorna';
                  return (
                    <tr key={r.chiave} className={sceglibile ? undefined : 'spenta'}>
                      <td>
                        {sceglibile && (
                          <input
                            type="checkbox"
                            aria-label={`Importa ${r.firstName} ${r.lastName}`}
                            checked={spunte.has(r.chiave)}
                            onChange={(e) => {
                              const n = new Set(spunte);
                              if (e.target.checked) n.add(r.chiave);
                              else n.delete(r.chiave);
                              setSpunte(n);
                            }}
                          />
                        )}
                      </td>
                      <td title={r.righe}>
                        {r.da.map((d) => (
                          <span key={d} className="da" title={FONTE[d]}>
                            {d}
                          </span>
                        ))}
                      </td>
                      <td>
                        {r.firstName} {r.lastName}
                      </td>
                      <td className="tel">{r.phone ?? '—'}</td>
                      <td>{r.email ?? '—'}</td>
                      <td>
                        <span className={`pillola ${r.esito}`}>{r.esito}</span>
                        {r.perché && <div className="perche">{r.perché}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Guasto errore={scrivi.error} />
          <div className="azioni">
            <button
              type="button"
              disabled={spunte.size === 0 || scrivi.isPending}
              onClick={() => scrivi.mutate({ file: letti, apply: true, scelte: [...spunte] })}
            >
              {scrivi.isPending
                ? 'Un attimo…'
                : spunte.size === 1
                  ? 'Importa la riga scelta'
                  : `Importa le ${spunte.size} righe scelte`}
            </button>
            <button
              type="button"
              className="bordato"
              disabled={aggiornabili.length === 0}
              onClick={() => aggiungi(aggiornabili)}
            >
              Spunta anche quelli da aggiornare ({aggiornabili.length})
            </button>
            <button
              type="button"
              className="bordato"
              disabled={soloRubrica.length === 0}
              onClick={() => aggiungi(soloRubrica)}
            >
              Spunta anche quelli solo in rubrica ({soloRubrica.length})
            </button>
            <button type="button" className="bordato" onClick={daCapo}>
              Scegli altri file
            </button>
          </div>
        </>
      )}

      {fase === 3 && scrivi.data && (
        <>
          <p className="riuscito">
            Fatto: {scrivi.data.creati} {scrivi.data.creati === 1 ? 'socio nuovo' : 'soci nuovi'},{' '}
            {scrivi.data.aggiornati} {scrivi.data.aggiornati === 1 ? 'aggiornato' : 'aggiornati'}.
          </p>
          <p className="tenue piccolo">
            Le righe non scelte e quelle scartate non sono entrate. Il registro delle operazioni tiene chi ha
            importato, quando, e da quale file.
          </p>
          <div className="azioni">
            <button type="button" onClick={chiudi}>
              Torna all’elenco
            </button>
            <button type="button" className="bordato" onClick={daCapo}>
              Importa altri file
            </button>
          </div>
        </>
      )}
    </section>
  );
}
